import {
    Component,
    ElementRef,
    inject,
    input,
    output,
    viewChild,
    effect,
    signal,
    computed,
    untracked,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { DeliveryNoteBundlesService } from 'api/services';
import { lastValueFrom } from 'rxjs';
import {
    TableRowData,
    hasBundleKeys,
    hasKey,
    hasAlternateKey,
    hasOrgSiteKey,
} from '@app/shared/models/table-data-types';
import { ColumnConfig } from '@app/shared/services/view-configs/column-creator';
import { AutoRefreshBlockerService } from '@app/shared/services/auto-refresh-blocker.service';

@Component({
    selector: 'app-bundle-name-edit',
    standalone: true,
    imports: [
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        TranslateModule,
    ],
    template: `
        <div class="tw-flex tw-items-center tw-w-full tw-group">
            <!-- Read-only mode -->
            @if (!isEditing()) {
                <!-- Read-only display (no editing allowed) -->
                @if (readOnly()) {
                    <div class="tw-w-full tw-text-gray-700 tw-py-1">
                        {{ displayValue() || '—' }}
                    </div>
                }
                <!-- Editable display -->
                @if (!readOnly()) {
                    <!-- With existing bundle name -->
                    @if (displayValue()) {
                        <div
                            class="tw-cursor-pointer tw-w-full tw-px-2 tw-py-1 tw-rounded tw-border tw-border-transparent hover:tw-border-blue-200 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-group"
                            (click)="startEditing()"
                            [matTooltip]="
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.EDIT-BUNDLE-NAME'
                                    | translate
                            ">
                            <div
                                class="tw-flex tw-items-center tw-justify-between">
                                <span class="tw-text-gray-900">{{
                                    displayValue()
                                }}</span>
                                <mat-icon
                                    class="tw-text-gray-400 group-hover:tw-text-blue-500 tw-transition-colors tw-duration-200 tw-text-sm tw-opacity-70 group-hover:tw-opacity-100"
                                    fontSet="material-symbols-outlined">
                                    edit
                                </mat-icon>
                            </div>
                        </div>
                    }
                    <!-- No bundle name yet -->
                    @if (!displayValue()) {
                        <div
                            class="tw-cursor-pointer tw-w-full tw-px-2 tw-py-1 tw-rounded tw-border tw-border-transparent hover:tw-border-blue-200 hover:tw-bg-blue-50 tw-transition-all tw-duration-200 tw-group"
                            (click)="startEditing()"
                            [matTooltip]="
                                'OUTGOING-GOODS.FOLDER-VIEW.HOVER-TEXT.EDIT-BUNDLE-NAME'
                                    | translate
                            ">
                            <div
                                class="tw-flex tw-items-center tw-justify-between">
                                <span class="tw-text-gray-500 tw-italic">{{
                                    'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME'
                                        | translate
                                }}</span>
                                <mat-icon
                                    class="tw-text-gray-400 group-hover:tw-text-blue-500 tw-transition-colors tw-duration-200 tw-text-sm tw-opacity-70 group-hover:tw-opacity-100"
                                    fontSet="material-symbols-outlined">
                                    edit
                                </mat-icon>
                            </div>
                        </div>
                    }
                }
            }

            <!-- Edit mode -->
            @if (isEditing()) {
                <div
                    class="tw-flex tw-items-center tw-w-full tw-px-1 tw-py-0.5 tw-rounded tw-border tw-border-blue-200 tw-bg-blue-50 tw-transition-all tw-duration-200">
                    <input
                        #inputRef
                        type="text"
                        class="tw-flex-1 tw-border-none tw-outline-none tw-px-1 tw-py-0 tw-text-sm tw-bg-transparent tw-text-gray-900"
                        [placeholder]="
                            'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME'
                                | translate
                        "
                        [style.min-width.ch]="8"
                        minlength="1"
                        maxlength="100"
                        [(ngModel)]="editValue"
                        (keydown.enter)="saveBundleName()"
                        (keydown.escape)="cancelEdit()" />
                    <div class="tw-flex tw-items-center">
                        <button
                            aria-label="save-bundle-name"
                            (click)="saveBundleName()"
                            class="tw-p-0.5 tw-rounded tw-text-green-600 hover:tw-bg-green-100 tw-transition-colors tw-duration-200"
                            mat-icon-button>
                            <mat-icon
                                class="tw-text-lg"
                                fontSet="material-symbols-outlined">
                                save
                            </mat-icon>
                        </button>
                        <button
                            aria-label="cancel-edit-bundle-name"
                            (click)="cancelEdit()"
                            class="tw-p-0.5 tw-rounded tw-text-gray-500 hover:tw-bg-gray-100 tw-transition-colors tw-duration-200"
                            mat-icon-button>
                            <mat-icon
                                class="tw-text-lg"
                                fontSet="material-symbols-outlined">
                                cancel
                            </mat-icon>
                        </button>
                    </div>
                </div>
            }
        </div>
    `,
})
export class BundleNameEditComponent {
    value = input<string>('');
    row = input.required<TableRowData>();
    column = input<ColumnConfig>();
    index = input<number>(0);
    item = input<TableRowData>();
    parentRow = input<TableRowData>();
    organizationSiteKey = input<string>('');
    readOnly = input<boolean>(false);
    orgType = input<'consignor' | 'carrier' | 'consignee'>('consignor');
    valueChanged = output<string>();
    refresh = output<void>();

    inputRef = viewChild<ElementRef>('inputRef');

    isEditing = signal(false);
    editValue = '';

    // Local value to display after successful
    private localValue = signal<string | null>(null);
    displayValue = computed(() => this.localValue() ?? this.value());

    private refreshBlocker = inject(AutoRefreshBlockerService);

    constructor(
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private toastService: ToastService,
        private translateService: TranslateService
    ) {
        // Watch for row/value changes and cancel edit mode when they change
        effect(() => {
            this.row(); // track row changes
            this.value(); // track value changes
            // Avoid tracking isEditing itself, otherwise switching to edit mode
            // retriggers this effect and instantly closes the editor.
            if (untracked(() => this.isEditing())) {
                this.cancelEdit();
            }
        });

        // Pause the table auto-reload while the user is typing here; a reload
        // would swap the row input and cancel the edit, losing the text.
        effect(onCleanup => {
            if (this.isEditing()) {
                const release = this.refreshBlocker.acquire('inline-edit');
                onCleanup(release);
            }
        });
    }

    startEditing(): void {
        if (this.readOnly()) {
            return;
        }

        this.isEditing.set(true);
        // Use displayValue to get the most current value (including local updates)
        this.editValue = this.displayValue() || '';

        // Focus the input after the view updates
        setTimeout(() => {
            const inputRef = this.inputRef();
            if (inputRef) {
                inputRef.nativeElement.focus();
                inputRef.nativeElement.select();
            }
        });
    }

    cancelEdit(): void {
        this.isEditing.set(false);
        this.editValue = '';
    }

    async saveBundleName(): Promise<void> {
        try {
            const bundleName = this.editValue?.trim() || '';

            // Validate the bundle name
            if (!bundleName) {
                this.toastService.error(
                    this.translateService.instant('GENERAL.VALIDATION.REQUIRED')
                );
                return;
            }

            if (bundleName.length > 100) {
                this.toastService.error(
                    this.translateService.instant(
                        'GENERAL.VALIDATION.MAX-LENGTH',
                        { max: 100 }
                    )
                );
                return;
            }

            const rowData = this.row() || this.item();
            const siteKey =
                this.organizationSiteKey() ||
                (hasOrgSiteKey(rowData) ? rowData.orgSiteKey : '');

            let bundleKey: string | undefined;
            if (hasKey(rowData)) {
                bundleKey = rowData._key;
            } else if (hasAlternateKey(rowData)) {
                bundleKey = rowData.key;
            }

            if (!bundleKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            // Validate bundle keys exist
            if (!hasBundleKeys(rowData)) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            // Call the bundle patch API
            await lastValueFrom(
                this.deliveryNoteBundlesService.patchBundle({
                    organizationSiteKey: siteKey,
                    deliveryNoteBundleKey: bundleKey,
                    body: {
                        name: bundleName,
                        consignorKey: rowData.consignorKey,
                        carrierKey: rowData.carrierKey,
                        consigneeKey: rowData.consigneeKey,
                    },
                })
            );

            // Close edit mode
            this.isEditing.set(false);
            this.editValue = '';

            // Update local value to display the new bundle name immediately
            // (necessary because ngComponentOutlet doesn't support output bindings)
            this.localValue.set(bundleName);

            // Emit events (kept for potential future use or other binding methods)
            this.valueChanged.emit(bundleName);
            this.refresh.emit();

            this.toastService.success(
                this.translateService.instant(
                    'OUTGOING-GOODS.FOLDER-VIEW.BUNDLE-NAME-UPDATE-SUCCESS'
                )
            );
        } catch (error) {
            this.toastService.error(
                this.translateService.instant('GENERAL-ERROR')
            );
        }
    }
}
